import { useCallback, useEffect, useRef } from "react";

declare global {
	interface Window {
		turnstile?: {
			render: (container: HTMLElement, options: Record<string, unknown>) => string;
			reset: (widgetId: string) => void;
			remove: (widgetId: string) => void;
		};
	}
}

const TURNSTILE_SCRIPT_ID = "cf-turnstile-script";
const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileWidgetProps = {
	siteKey?: string;
	onVerify: (token: string) => void;
	onExpire?: () => void;
};

const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ siteKey, onVerify, onExpire }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetIdRef = useRef<string | null>(null);
	const onVerifyRef = useRef(onVerify);
	const onExpireRef = useRef(onExpire);
	onVerifyRef.current = onVerify;
	onExpireRef.current = onExpire;

	const renderWidget = useCallback(() => {
		if (!containerRef.current || !window.turnstile || !siteKey) return;
		if (widgetIdRef.current) return;
		widgetIdRef.current = window.turnstile.render(containerRef.current, {
			sitekey: siteKey,
			callback: (token: string) => onVerifyRef.current(token),
			"expired-callback": () => onExpireRef.current?.(),
			theme: "light",
			size: "normal",
		});
	}, [siteKey]);

	useEffect(() => {
		if (!siteKey) return undefined;

		// Load script if not already present
		if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
			const script = document.createElement("script");
			script.id = TURNSTILE_SCRIPT_ID;
			script.src = TURNSTILE_SCRIPT_URL;
			script.async = true;
			script.onload = () => renderWidget();
			document.head.appendChild(script);
		} else if (window.turnstile) {
			renderWidget();
		}

		return () => {
			if (widgetIdRef.current && window.turnstile) {
				window.turnstile.remove(widgetIdRef.current);
				widgetIdRef.current = null;
			}
		};
	}, [siteKey, renderWidget]);

	if (!siteKey) return null;

	return <div ref={containerRef} />;
};

export default TurnstileWidget;
